import { el, setChildren } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import {
  buildActivityForDisplay
} from 'root/startup-view/calendar/details/utilities';

import MeetingStatusControl from 'root/startup-view/calendar/details/body/displays/type-specific-content/meeting-status-control';
import ProfilePictureDisplay from 'root/startup-view/inbox/profile-picture-display';
import MoreActionsWidget from 'root/startup-view/calendar/details/header/more-actions-widget';

import PublicMeetingEditor from './public-meeting-editor';
import ActivityNotExistsView from './activity-not-exists-view';

import {
  determineUser
} from './utilities';

import './index.scss';

class PublicMeetingView {

  constructor(configuration) {

    this.activityId = configuration.activityId;
    this.activity = null;

    this.profilePictureDisplay = new ProfilePictureDisplay();
    this.meetingStatusControl = null;
    this.moreActionsWidget = null;

    this.startupUser = el('h5.calendar-activity-entry-name', {
      class: 'ellipsis'
    });

    this.el = el('#public-meeting-view-main', [
      el('ht-spinner')
    ]);

  }

  renderMoreActionsWidget() {

    const widget = new MoreActionsWidget({
      activityDetails: this.activityDetails,
      onDelete: this.handleActivityDelete.bind(this),
      user: this.user,
      onUninviteAttendee: this.uninviteAttendee.bind(this),
      onUninviteAttendeeFromUpcomingMeetings: this.uninviteAttendeeFromUpcomingMeetings.bind(this),
    });

    widget.update(this.activity);

    return widget;

  }

  renderMeetingStatusControl() {

    const display = new MeetingStatusControl({
      activity: this.activity,
      onChange: this.handleContentChange.bind(this),
      userId: this.user._id,
      accept: this.acceptMeeting.bind(this),
      acceptAll: this.acceptAllMeetings.bind(this),
      decline: this.declineMeeting.bind(this),
      declineAll: this.declineAllMeetings.bind(this),
      mayAttend: this.mayAttendMeeting.bind(this),
      mayAttendAll: this.mayAttendAllMeetings.bind(this)
    });

    return display;

  }

  displayActivity() {

    const updatorPanel = new PublicMeetingEditor({
      meeting: this.activity.event,
      onSave: this.rescheduleMeeting.bind(this),
      onCancel: this.cancelMeeting.bind(this),
      readOnly: true,
      onRemindersChange: this.handleRemindersChange.bind(this)
    });

    const creator = this.activity.event.creator;

    this.startupUser.textContent = creator.fullName || creator.email;
    this.profilePictureDisplay.update(creator);

    this.moreActionsWidget = this.renderMoreActionsWidget();
    this.meetingStatusControl = this.renderMeetingStatusControl();

    const container = el('.test', [
      this.typeSpecificContentContainer
    ]);
    setChildren(this.el, [el('#public-meeting-header-main', [
      el('#public-meeting-header-content', [
        this.profilePictureDisplay,
        this.startupUser,
        container
      ]),
      el('#public-meeting-header-actions', [
        this.moreActionsWidget
      ])
    ]), el('#public-meeting-body-main', [
      updatorPanel
    ])]);

    setChildren(container, [this.meetingStatusControl]);

  }

  displayActivityNotExistsView() {

    setChildren(this.el, [new ActivityNotExistsView()]);

  }

  handleActivityDelete() {

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Meeting deleted! You will be redirected to /community page in 5 seconds!',
      seconds: 5,
      type: 'info'
    });

    setTimeout(() => {
      window.location.href = '/community';
    }, 5000);
  }

  async loadActivity() {

    const result = await serverInterface.startup.publicCalendarActivities.getPopulatedActivityById(this.activityId);

    this.activity = buildActivityForDisplay(result);
    this.user = determineUser(result);

  }

  async initialize() {

    try {

      await this.loadActivity();

      this.displayActivity();

    } catch (exception) {

      if (exception.code !== 'CALENDAR_ACTIVITY_NOT_FOUND' || exception.code !== 'CALENDAR_ACTIVITY_MUST_BE_PUBLIC') {
        // eslint-disable-next-line no-console
        console.error(exception);
      }

      this.displayActivityNotExistsView();

    }

  }

  async rescheduleMeeting(data) {

    try {

      await serverInterface.startup.publicCalendarActivities.meetings.rescheduleMeeting(this.activity._id, this.activity.event._id, data);

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Appointment rescheduled successfully!',
        seconds: 5,
        type: 'info'
      });

    } catch (exception) {

      if (exception.code === 'SLOTS_ALREADY_BLOCKED') {
        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: 'User is not available in the selected time. Please try different timings!',
          seconds: 5,
          type: 'error'
        });
      } else {
        throw exception;
      }

    }

  }

  async cancelMeeting() {

    const confirmed = await application.prompts.confirm({
      header: 'Cancel this appointment?',
      description: 'This cancels the appointment for everyone and notifies all attendees. This cannot be undone.',
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'No'
    });

    if (confirmed !== true) {
      return;
    }

    await serverInterface.startup.publicCalendarActivities.meetings.cancelMeeting(this.activity._id, this.activity.event._id);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Appointment cancelled successfully!',
      seconds: 5,
      type: 'info'
    });

  }

  async handleRemindersChange(event) {

    const activityId = this.activity._id;
    const reminder = event.reminder;

    if (event.type === 'added') {
      const newReminder = await serverInterface.startup.publicCalendarActivities.reminders.setupReminder(activityId, event.reminder);
      reminder._id = newReminder._id;
    } else if (event.type === 'updated') {
      serverInterface.startup.publicCalendarActivities.reminders.updateReminderById(activityId, reminder._id, reminder);
    } else {
      serverInterface.startup.publicCalendarActivities.reminders.deleteReminderById(activityId, reminder._id);
    }

  }

  acceptMeeting(meetingId) {
    return serverInterface.startup.publicCalendarActivities.meetings.attendMeeting(this.activity._id, meetingId);
  }

  acceptAllMeetings(meetingId) {
    return serverInterface.startup.publicCalendarActivities.meetings.attendAllUpcomingMeetings(this.activity._id, meetingId);
  }

  declineMeeting(meetingId) {
    return serverInterface.startup.publicCalendarActivities.meetings.declineMeeting(this.activity._id, meetingId);
  }

  declineAllMeetings(meetingId) {
    return serverInterface.startup.publicCalendarActivities.meetings.declineAllUpcomingMeetings(this.activity._id, meetingId);
  }

  mayAttendMeeting(meetingId) {
    return serverInterface.startup.publicCalendarActivities.meetings.mayAttendMeeting(this.activity._id, meetingId);
  }

  mayAttendAllMeetings(meetingId) {
    return serverInterface.startup.publicCalendarActivities.meetings.mayAttendAllUpcomingMeetings(this.activity._id, meetingId);
  }

  uninviteAttendee(meetingId, activityId) {
    return serverInterface.startup.publicCalendarActivities.meetings.uninviteAttendee(this.activity._id);
  }

  uninviteAttendeeFromUpcomingMeetings(meetingId, activityId) {
    return serverInterface.startup.publicCalendarActivities.meetings.uninviteAttendeeFromUpcomingMeetings(this.activity._id);
  }

  handleContentChange() {

    window.location.reload();

  }

  onmount() {

    this.initialize();

  }

}

export default PublicMeetingView;